<template>
  <div v-loading="isLoading">
    <div class="search-bar">
      <el-input
        v-model="searchQuery"
        :label="$t('EmailRows_SearchLabel')"
        :placeholder="$t('EmailRows_SearchPlaceholder')"
        clearable
        @input="onSearchInput"
        @clear="onSearchClear"
        prefix-icon="el-icon-search"
      />
    </div>
    <div class="grid">
      <div class="card" v-for="item in items" :key="item.id">
        <!-- Preview -->
        <div class="card__preview">
          <iframe
            :srcdoc="item.content"
            style="border: 1px solid #EBEEF5;"
            height="250px"
            width="100%"
          />
        </div>

        <!-- Content -->
        <div class="card__title-row">
          <template v-if="editingTitleId === item.id">
            <el-input
              v-model="editingTitleValue"
              size="small"
              class="card__title-input"
              :disabled="savingTitle"
              @keyup.enter.native="saveTitle(item)"
              :data-qa="`rename-synced-row-input-${item.id}`"
            />
            <el-button
              type="primary"
              size="small"
              :loading="savingTitle"
              @click="saveTitle(item)"
              :data-qa="`rename-synced-row-save-${item.id}`"
              icon="icon-floppy"
            />
            <el-button
              size="small"
              :disabled="savingTitle"
              @click="cancelEditTitle"
              :data-qa="`rename-synced-row-cancel-${item.id}`"
              icon="icon-cancel-circled"
            >
            </el-button>
          </template>
          <template v-else>
            <h3 class="card__title">{{ item.title || item.name }}</h3>
            <i
              v-if="$can('EmailSavedRows::Update')"
              class="el-icon-edit card__title-edit"
              @click="startEditTitle(item)"
              :data-qa="`rename-synced-row-edit-${item.id}`"
            />
          </template>
        </div>

        <div class="card__status-row">
          <div
            class="card__sync-status"
            v-if="syncStatuses[item.id] !== undefined"
          >
            <template v-if="syncStatuses[item.id] === null">
              <i class="el-icon-loading" />
              <span>{{ $t('EmailRows_SyncStatus_Loading') }}</span>
            </template>
            <template v-else-if="syncStatuses[item.id].in_sync">
              <i class="el-icon-circle-check sync-status--in-sync" />
              <span class="sync-status--in-sync">{{
                $t('EmailRows_SyncStatus_InSync')
              }}</span>
            </template>
            <template v-else>
              <i class="el-icon-warning sync-status--stale" />
              <span class="sync-status--stale">{{
                $t('EmailRows_SyncStatus_Stale', {
                  stale: syncStatuses[item.id].stale_count,
                  total: syncStatuses[item.id].total_count
                })
              }}</span>
            </template>
          </div>

          <div class="card__meta">
            <span>{{
              $tc(
                'EmailRows_UsedInMessageCount',
                item.xpCampaignMessageCount || 0,
                { count: item.xpCampaignMessageCount || 0 }
              )
            }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="card__actions">
          <el-button
            v-if="$can('EmailSavedRows::Update')"
            type="primary"
            plain
            @click="openEditor(item)"
            :data-qa="`edit-synced-row-${item.id}`"
          >
            {{ $t('EmailRows_Action_EditSyncedRow') }}
          </el-button>

          <a
            class="btn-underlined"
            @click="viewDetails(item)"
            :data-qa="`view-synced-row-details-${item.id}`"
          >
            {{ $t('EmailRows_Action_ViewDetails') }}
          </a>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="isEditing"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      fullscreen
      custom-class="edit-synced-row-dialog"
      append-to-body
    >
      <edit-single-row-mode
        v-if="isEditing && editingRow"
        :saved-row="editingRow"
        :on-save="handleEditorSave"
        :on-cancel="handleEditorCancel"
      />
    </el-dialog>

    <div class="pagination-container" v-if="totalItems > pageSize">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="totalItems"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestApi from '@/core/services/RestApi'
import EditSingleRowMode from '@/common/components/email/EditSingleRowMode'

export default {
  name: 'SyncedEmailSavedRowsListPage',

  components: {
    EditSingleRowMode
  },

  data() {
    return {
      items: [],
      syncStatuses: {},
      isLoading: false,
      currentPage: 1,
      pageSize: 9,
      searchQuery: '',
      searchDebounce: null,
      totalItems: 0,
      isEditing: false,
      editingRow: null,
      syncPollTimers: {},
      editingTitleId: null,
      editingTitleValue: '',
      savingTitle: false
    }
  },

  computed: {
    pageTitle() {
      return this.$t('EmailRows_Tab_Synced')
    }
  },

  mounted() {
    this.fetchData()
  },

  beforeDestroy() {
    Object.values(this.syncPollTimers).forEach(t => clearTimeout(t))
  },

  methods: {
    onSearchInput() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1
        this.fetchData()
      }, 300)
    },

    onSearchClear() {
      this.currentPage = 1
      this.fetchData()
    },
    async fetchData() {
      this.isLoading = true
      this.syncStatuses = {}
      Object.values(this.syncPollTimers).forEach(t => clearTimeout(t))
      this.syncPollTimers = {}
      try {
        const endpoint = projectEndpoint({
          method: 'get',
          url: 'email-saved-rows',
          params: {
            synced: 1,
            page: this.currentPage,
            'per-page': this.pageSize,
            'include-stats': 1,
            ...(this.searchQuery ? { title: this.searchQuery } : {})
          }
        })

        const response = await RestApi(endpoint)
        this.items = response.data
        this.totalItems = parseInt(
          response.headers['x-pagination-total-count'] || 0
        )

        this.fetchSyncStatuses(this.items)
      } catch (error) {
        this.$message.error(this.$t('EmailRows_FetchError'))
      } finally {
        this.isLoading = false
      }
    },

    fetchSyncStatus(id) {
      this.$set(this.syncStatuses, id, null)
      RestApi.get(`projects/${window.xp.project.id}/content/${id}/sync-status`)
        .then(({ data }) => {
          this.$set(this.syncStatuses, id, data)
        })
        .catch(() => {
          this.$delete(this.syncStatuses, id)
        })
    },

    fetchSyncStatuses(items) {
      items.forEach(item => {
        this.fetchSyncStatus(item.id)
      })
    },

    handlePageChange(page) {
      this.currentPage = page
      this.fetchData()
    },

    openEditor(item) {
      this.editingRow = item
      this.isEditing = true
    },

    handleEditorSave() {
      const saved = this.editingRow
      this.isEditing = false
      this.editingRow = null
      this.$message.success(this.$t('EmailRows_EditSuccess'))
      this.fetchData()
      if (saved) {
        this.startItemSyncPolling(saved.id, 0)
      }
    },

    startItemSyncPolling(id, count) {
      if (this.syncPollTimers[id]) {
        clearTimeout(this.syncPollTimers[id])
      }
      const delay = count < 5 ? 1000 : 4000
      this.syncPollTimers[id] = setTimeout(
        () => this.pollItemSyncStatus(id, count),
        delay
      )
    },

    async pollItemSyncStatus(id, count) {
      try {
        const endpoint = projectEndpoint({
          method: 'get',
          url: `content/${id}/sync-status`
        })
        const { data } = await RestApi(endpoint)
        this.$set(this.syncStatuses, id, data)
        if (!data.in_sync) {
          this.startItemSyncPolling(id, count + 1)
        } else {
          delete this.syncPollTimers[id]
        }
      } catch (e) {
        delete this.syncPollTimers[id]
      }
    },

    handleEditorCancel() {
      this.isEditing = false
      this.editingRow = null
    },

    viewDetails(item) {
      this.$router.push(
        projectRoute({
          name: 'EmailSavedRowsDetails',
          query: { id: item.id }
        })
      )
    },

    startEditTitle(item) {
      this.editingTitleId = item.id
      this.editingTitleValue = item.title || item.name || ''
    },

    cancelEditTitle() {
      this.editingTitleId = null
      this.editingTitleValue = ''
    },

    async saveTitle(item) {
      const name = (this.editingTitleValue || '').trim()
      if (!name) {
        this.$message.error(this.$t('EmailRows_RenameEmptyError'))
        return
      }

      this.savingTitle = true
      try {
        const endpoint = projectEndpoint({
          method: 'patch',
          url: `content/${item.id}/rename-synced`,
          data: { name }
        })
        const { data } = await RestApi(endpoint)

        if (!data.title) {
          data.title = data.name
        }

        Object.assign(item, data)

        this.editingTitleId = null
        this.editingTitleValue = ''
        this.$message.success(this.$t('EmailRows_RenameSuccess'))

        this.fetchSyncStatus(item.id)
      } catch (error) {
        this.$message.error(this.$t('EmailRows_RenameError'))
      } finally {
        this.savingTitle = false
      }
    }
  }
}
</script>

<style lang="scss">
.edit-synced-row-dialog {
  display: flex;
  flex-direction: column;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .edit-single-row-modal {
    width: 100% !important;
    max-width: 100% !important;
    height: 100% !important;
    border-radius: 0 !important;
  }
}
</style>

<style lang="scss" scoped>
.search-bar {
  max-width: 400px;
  margin-bottom: 16px;
}
.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 43px;
  grid-row-gap: 34px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  box-shadow: 4px 4px 18px 0 #0000000d;
  padding: 15px;

  &__preview {
    display: flex;
    height: 257px;
    justify-content: space-between;
    align-items: center;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 5px 1px;
    margin-bottom: 16px;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;

    .el-button {
      margin-left: 0;
      padding: 9px 9px;
      min-width: 36px;
    }
  }

  &__title {
    color: #4b515f;
    margin: 0;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__title-edit {
    cursor: pointer;
    font-size: 16px;
    flex-shrink: 0;
  }

  &__title-input {
    flex: 1;
  }

  &__status-row {
    display: flex;
    align-items: center;
    margin-top: 8px;
    gap: 6px;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #4b515f;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
  }

  &__sync-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    line-height: 16px;
  }

  &__actions {
    display: flex;
    gap: 17px;
    margin-top: 17px;
    align-items: center;
  }
}

.sync-status--in-sync {
  color: #67c23a;
}

.sync-status--stale {
  color: #e6a23c;
}

.btn-underlined {
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-decoration: underline;
  color: #1d87c9;
  cursor: pointer;

  &.disabled {
    color: #dbdee7;
    cursor: not-allowed;
    text-decoration: underline;
  }
}

.pagination-container {
  margin-top: 40px;
  display: flex;
  justify-content: center;
}

>>> .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #1d87c8;
}
</style>



// WEBPACK FOOTER //
// src/modules/email-saved-rows/pages/EmailRowsSyncedListPage.vue